// Services
import breedingManagementService from "@/app/services/main/breeding-management/breeding-management-service.js";
import breedService from "@/app/services/main/breed/breed-service.js";

const FileUpload = () =>
    import(
        /* webpackPrefetch: true, webpackChunkName: "file-upload" */ "@/app/views/main-view/_shared/_components/file-upload/file-upload.vue"
    );
const MatingDamSireDetails = () =>
    import(/*webpackChunkName: "mating-dam-sire-details-container" */ "@/app/views/main-view/breeding-management/puppy-management/containers/litter-list-container/components/mating-dam-sire-details-container/mating-dam-sire-details-container.vue")

// Mixins
import { dateMixins } from "@/app/mixins/utils/date-mixins";
import { toastMessageMixins } from "@/app/mixins/utils/toast-message-mixins";
import { imageCompressorMixins } from "@/app/mixins/utils/image-compressor-mixins";
import { unsavedChangesModalMixins } from "@/app/mixins/utils/unsaved-changes-mixins.js";

// Constants
import { STATUS_CODES, SUCCESS_MSG, LITTER_STATUS } from "@/app/constants/status-constants";
import { BREEDER_ROUTES } from "@/app/constants/breeder-routes-constants";
import PUPPY_CARE_INFO from "@/app/views/main-view/breeding-management/gestation-period/constants/puppy-care.info.js";

// Third Party libraries
import DatePicker from "vue2-datepicker";
// Components

export default {
    name: "AddLitterContainer",
    components: { 
        DatePicker, 
        FileUpload,
        MatingDamSireDetails
    },
    mixins: [dateMixins, toastMessageMixins, unsavedChangesModalMixins, imageCompressorMixins],
    data() {
        return {
            imageTypeList: ['image/svg','image/png','image/jpg','image/jpeg','image/gif','image/raw', 'image/webp'],
            isAPILoading: false,
            isAPISubmitLoading: false,
            matingId: null,

            breedList: [],

            matingDetails: null,
            littersCount: 0,
            matingName: "",
            damDetails: null,
            sireDetails: null,

            genderOption: ["Male", "Female"],

            puppyList: [this.createNewForm()],
            initialPuppyList: [this.createNewForm()],

            maxWeight: 99999.99,
            maxPuppiesCount: 15,

            isSavingLittersSuccess: false,
            prevRoute: "Puppy List",

            currentPuppyIndex: 0, 
            
            dateOfBirth: null,

            typeOfBirth: null,
            whelpDate: null,

            PUPPY_CARE_INFO,
            isPuppyCareInfoExpanded: false,
        };
    },
    computed: {
        showAddMoreLitters(){
			return (this.puppyList.length + parseFloat(this.littersCount || 0)) !== this.maxPuppiesCount;
		},
    },
    async created() {
        await this.getBreedList();

        if (this.$route.query) {
            await this.getDamAndSireMatingDetails(this.$route.query.id);

            this.matingId = this.$route.query.id;

            if(this.$route.query.from === "mating-details"){
                this.prevRoute = "Mating Details";
            } else if(this.$route.query.from === "mating-list"){
                this.prevRoute = "Mating List";
            }
        }
    },
    methods: {
        async onSubmitDetails(){
            try{
                this.isAPISubmitLoading = true;
                const littersWithValues = this.puppyList.filter(puppy => {
                    const { gender, identifyingMarks, collarColour, name, breed, dateOfBirth, birthWeight, dogImageUploadPreviewSource, microchipNumber } = puppy;
                
                    const hasOtherFieldsFilled = 
                        gender || 
                        (identifyingMarks && identifyingMarks.trim()) || 
                        (collarColour && collarColour.trim()) || 
                        (name && name.trim()) || 
                        breed || 
                        dateOfBirth || 
                        birthWeight ||
                        microchipNumber;
                
                    
                    return hasOtherFieldsFilled || dogImageUploadPreviewSource;
                });
                
                // If there are only images and no inputted values, will show swal and retain existing forms
                const hasOnlyImages = littersWithValues.every(puppy => puppy.dogImageUploadPreviewSource && !(
                    puppy.gender || 
                    (puppy.identifyingMarks && puppy.identifyingMarks.trim()) || 
                    (puppy.collarColour && puppy.collarColour.trim()) || 
                    (puppy.name && puppy.name.trim()) || 
                    puppy.breed || 
                    puppy.dateOfBirth || 
                    puppy.birthWeight
                ));
                
                this.puppyList = littersWithValues.length === 0 ? [this.createNewForm()] : littersWithValues;
                
                if (littersWithValues.length === 0 || hasOnlyImages) {
                    await this.$swal({
                        text: "Please fill out at least one litter detail along with an image.",
                        imageHeight: "30px",
                        imageUrl: `${require("@/assets/images/_shared/sad-dog.png")}`,
						imageAlt: "Sad Dog",
                        confirmButtonText: 'Got it',
                        showCloseButton: true,
                    })

                    return;
                }

                const littersToSave = this.puppyList.map(puppy => {
                    const { id = null, gender, identifyingMarks, collarColour, name, breed, dateOfBirth, birthWeight, microchipNumber } = puppy;

                    const payload = {
                        id,
                        breedId: breed ? breed.id : null,
                        matingId: this.matingId,
                        name,
                        status: LITTER_STATUS.ACTIVE,
                        dateOfBirth: dateOfBirth ? this.dateToISO(dateOfBirth) : null,
                        identifyingMarks,
                        collarColour,
                        birthWeight,
                        unitsOfWeight: "kg",
                        gender,
                        breederSysId: this.matingDetails.breederSysId,
                        microchipNumber
                    }

                    return payload;
                })

                // Filter again to remove empty puppies with image only
                const littersToSaveWithValues = littersToSave.filter(puppy => {
                    const { gender, identifyingMarks, collarColour, name, breedId, dateOfBirth, birthWeight } = puppy;
                
                    const hasValues = 
                        gender || 
                        (identifyingMarks && identifyingMarks.trim()) || 
                        (collarColour && collarColour.trim()) || 
                        (name && name.trim()) || 
                        breedId || 
                        dateOfBirth || 
                        birthWeight;
            
                    return hasValues;
                });

                const littersImageUploads = this.puppyList.map((puppy, index) => {  
                    return puppy.dogImageUpload && puppy.dogImageUpload.length ? puppy.dogImageUpload[0] : null    
                });

                const littersSavingResponse = await breedingManagementService.saveLittersDetails(littersToSaveWithValues, littersImageUploads);

                const { SUCCESS_ADD_LITTER } = this.toastMessages.litters();

                if(littersSavingResponse.status === STATUS_CODES.SUCCESS){
                    // check if every puppy has been saved successfully
                    if(littersSavingResponse.data){
                        const areAllSavedSuccessfully = [...littersSavingResponse.data].every(puppy => puppy.id && puppy.status === LITTER_STATUS.ACTIVE);
                    
                        if(areAllSavedSuccessfully){
                            this.showCustomSuccessToast(SUCCESS_MSG.SUCCESS_TITLE, SUCCESS_ADD_LITTER);
                        }
                    }

                    this.isSavingLittersSuccess = true; // will prevent the unsaved changes modal from showing
                    this.$router.push(`${BREEDER_ROUTES.LITTER_LIST}?id=${this.matingId}&from=${this.$route.query.from}`)
                } else{
                    await this.$swal({
                        html: "<span>An error occurred while saving the added puppies. <br> <strong>Please try saving again.</strong> </span>",
                        imageHeight: "30px",
                        imageUrl: `${require("@/assets/images/_shared/sad-dog.png")}`,
						imageAlt: "Sad Dog",
                        confirmButtonText: 'Got it',
                        showCloseButton: true,
                    })
                }

                // get the data return back by POST to have the saved values with IDs in order to differentiate them from the new data
                if(littersSavingResponse && littersSavingResponse.data){

                    this.puppyList = [...littersSavingResponse.data].map((puppy, index) => {
                        const { dateOfBirth, breedId } = puppy;

                        const breed = breedId ? this.breedList.find(breedItem => breedItem.id === breedId) : null;
    
                        const existingPuppy = this.puppyList[index] || {};

                        return {
                            ...puppy,
                            breed,
                            dateOfBirth: dateOfBirth ? new Date(dateOfBirth) : null,
                            dogImageUploadPreviewSource: puppy.dogImageUploadPreviewSource ? puppy.dogImageUploadPreviewSource : existingPuppy.dogImageUploadPreviewSource || "",
                            dogImageToCrop: existingPuppy.dogImageToCrop || "",
                            dogImageUpload: existingPuppy.dogImageUpload || "",
                            imageToCrop: existingPuppy.imageToCrop || "",
                        }
                    })
                }

                // deep copy
                this.initialPuppyList = JSON.parse(JSON.stringify(this.puppyList));

            } catch(err){
                this.showGenericErrorToast();
            } finally{
                this.isAPISubmitLoading = false;
            }
        },
        async getDamAndSireMatingDetails(matingId) {
            if (!matingId) {
                return;
            }

            try {
                this.isAPILoading = true;
                const matingDetailsResponse =
                    await breedingManagementService.getDamAndSireMatingDetails("mating",
                        matingId
                    );

                if (matingDetailsResponse.status === STATUS_CODES.SUCCESS) {
                    const { data: matingDetails } = matingDetailsResponse;

                    this.damDetails = matingDetails.damDetails;
                    this.sireDetails = matingDetails.sireDetails;
                    this.matingName = matingDetails.alias;
                    this.littersCount = matingDetails.littersCount;
                    this.matingDetails = matingDetails;
                    if (matingDetails.damDetails && matingDetails.damDetails.whelpingDates && matingDetails.damDetails.whelpingDates.length > 0) {
                        const lastRecord = matingDetails.damDetails.whelpingDates[matingDetails.damDetails.whelpingDates.length - 1];
                        if (lastRecord.matingId === matingDetails.id) {
                            if (lastRecord.typeOfBirth) {
                                this.typeOfBirth = lastRecord.typeOfBirth;
                            }
                            if (lastRecord.whelpingDate) {
                                this.whelpDate = this.formatDateWithoutConversion(lastRecord.whelpingDate);

                                this.puppyList[0].dateOfBirth = this.whelpDate;
                                this.initialPuppyList[0].dateOfBirth = this.whelpDate;
                            }
                        }
                    }
                } else {
                    const { ERROR_GET_MATING_DETAILS } =
                        this.toastMessages.mating();
                    this.showAPIErrorToast(
                        ERROR_GET_MATING_DETAILS,
                        matingDetailsResponse
                    );
                }
            } catch (err) {
                this.showGenericErrorToast();
            } finally {
                this.isAPILoading = false;
            }
        },
        hasImageUrl(source){
            if(!source){
                return require('@/assets/images/_shared/dog-purchase.png');
            }

            return source;
        },
        sortAlphabetically(items) {
            const sortedItems = items.sort(function (a, b) {
                return a.name.localeCompare(b.name);
            });
            return sortedItems;
        },
        async getBreedList() {
            try {
                this.isAPILoading = true;
                const breedsResponse = await breedService.getBreeds();

                if (breedsResponse.status === STATUS_CODES.SUCCESS) {
                    const otherBreedOptions = breedsResponse.data.data.slice(0, 4);
                    const breeds = this.sortAlphabetically(breedsResponse.data.data.slice(4, breedsResponse.data.count));

                    // remove "Breed not specified", it has id = 0
                    this.breedList = [...otherBreedOptions, ...breeds].filter(
                        (breed) => breed.id != 0
                    );
                } else {
                    const { ERROR_GET_BREED_LIST } = this.toastMessages.breed();
                    this.showAPIErrorToast(ERROR_GET_BREED_LIST, breedsResponse);
                }
            } catch (err) {
                this.showGenericErrorToast();
            } finally {
                this.isAPILoading = false;
            }
        },
        async removePuppyForm(index) {
            const result = await this.$swal({
                imageHeight: "30px",
                title: "Are you sure you want to remove this puppy?",
                imageUrl: `${require("@/assets/images/_shared/dog-confirm.png")}`,
                showCancelButton: true,
                confirmButtonColor: "#4B8BFF",
                cancelButtonColor: "#d33",
                confirmButtonText: "Confirm",
                cancelButtonText: "Cancel",
                reverseButtons: true,
            });

            if (result.isConfirmed) {
                this.puppyList.splice(index, 1);
            }
        },
        createNewForm() {
            // Returns a new form object with default values
            return {
              gender: "",
              identifyingMarks: "",
              collarColour: "",
              name: "",
              breed: null,
              dateOfBirth: this.lastDateOfBirth ? this.lastDateOfBirth : this.whelpDate,
              birthWeight: null,
              dogImageUploadPreviewSource: "",
              dogImageToCrop: "",
              dogImageUpload: "",
              imageToCrop: "",
              microchipNumber:""
            };
        },
        async onAddMorePuppy() {
            if(this.puppyList.length === this.maxPuppiesCount){
                return;
            }

            const newPuppy = this.createNewForm();  // Use fresh form for each new entry
            this.puppyList.push(newPuppy);
        },
        notAfterToday(date) {
            if (this.whelpDate) {
                return date > new Date(this.whelpDate);;
            }
            return date > new Date();
        },
        validateBirthWeight(event, index) {
            const field = event.target;
        
            setTimeout(() => {
                // Use the regex to match the pattern: digits followed by an optional decimal point and more digits
                const regex = /^\d*\.?\d*/;
        
                // Check if the field has a valid value (exists and is not null/undefined)
                if (field && field.value) {
                    // Execute the regex and check if a match was found
                    const match = regex.exec(field.value);
        
                    // If the match is found, use the first match; otherwise, set the value to an empty string
                    let sanitizedInput = match ? match[0] : '';
        
                    // Apply the sanitized input to the field
                    event.target.value = sanitizedInput;
        
                    // Set the sanitized value back to the model
                    this.$set(this.puppyList[index], 'birthWeight', sanitizedInput);
                }
            }, 0);
        },

        async onUploadImage(event, index) {
 
            // let valid = await this.validateImage(event.target.files[0]);
            this.currentPuppyIndex = index;
            let valid = await this.validateImageWithType(event.target.files[0], this.imageTypeList);

            // do nothing when user cancels Upload
            if (!valid) {
                // reset uploader
                this.$refs.imgUploader.value = "";
                return;
            }

            // compress image if more than 2mb
            const file = await this.compressImage(event.target.files[0]);

            this.puppyList[index].dogImageToCrop = [file];

            // show preview of pet image
            let reader = new FileReader();
        
            reader.onload = () => {
                this.puppyList[index].imageToCrop = reader.result;
                this.$refs.modalFileUpload[index].show();
            };

            // this.sendEventTrackingToGA(GA.REGISTRATION_PAGE.ADD_PET_IMAGE.ACTION, GA.REGISTRATION_PAGE.CATEGORY, GA.REGISTRATION_PAGE.ADD_PET_IMAGE.LABEL);
            reader.readAsDataURL(file);
        },

        dogImageSource(index) {
                let img = "";
                
                if (this.puppyList[index].dogImageUploadPreviewSource) {
                    img = this.puppyList[index].dogImageUploadPreviewSource;
                } else {
                    img = require("@/assets/images/_shared/default-profile-pic.png");
                }
                return img;
        },
       

        setImageCrop({ image, blob }) {
            this.puppyList[this.currentPuppyIndex].dogImageUploadPreviewSource = image;
            this.puppyList[this.currentPuppyIndex].dogImageToCrop = blob;
            this.puppyList[this.currentPuppyIndex].dogImageUpload = blob;
        },
        clearFileInput(event, index) {
            this.$refs[`imgUploader-${index}`].value = "";
        },
        beforeEnter(el) {
            el.style.opacity = 0;
          },
        enter(el, done) {
            el.offsetHeight; // trigger reflow
            el.style.transition = 'opacity 0.5s';
            el.style.opacity = 1;
             done();
        },
        leave(el, done) {
            el.style.transition = 'opacity 0.5s';
            el.style.opacity = 0;
            done();
        },
        removePropertiesInArray(array, properties) {
            return array.map(obj => {
                const newObj = { ...obj };
                properties.forEach(property => {
                    delete newObj[property];
                });
                return newObj;
            });
        },
        updateDateOfBirth(date) {
            this.lastDateOfBirth = date;
            this.puppyList.forEach(form => {
                form.dateOfBirth = date;
            });
        },
        togglePuppyCareInfo(){
            this.isPuppyCareInfoExpanded = !this.isPuppyCareInfoExpanded;
        }
    },

    async beforeRouteLeave(to, from, next) {
        // exclude the dogImageToCrop and imageToCrop property in the comparison
        const cleanedPuppyList = this.removePropertiesInArray(this.puppyList, ['dogImageToCrop', 'imageToCrop']);
        const cleanedInitialPuppyList = this.removePropertiesInArray(this.initialPuppyList, ['dogImageToCrop', 'imageToCrop']);

        const isFormModified = JSON.stringify(cleanedPuppyList) !== JSON.stringify(cleanedInitialPuppyList)

        if(!this.isSavingLittersSuccess && isFormModified){
            const result = await this.showUnsavedChangesModal();

            if (result.value) {
                next(true);
            } else {
                next(false);
            }

        } else {
            next(true);
        }
    },
};
